/**
 * @module core/aspects
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 * @preferred
 *
 */ /** */

// Disable no any because we will not be expecting specific types
// since this will be called from every function in the application.
/* tslint:disable:no-any*/
import { beforeMethod, Metadata, afterMethod, onThrowOfMethod } from 'aspect.js';
import { LoggerService } from '@core/logger/services/logger.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { Observable } from 'rxjs';
import * as CircularJSON from 'circular-json';

/**
 * Cross cutting convention that logs before, after and on exceptions.
 *
 * Does not get registered in the normal pipeline, but is seeked when @Wove is called
 * on an angular component or service.  Based on the pattern names, calls this file -
 * which is any pattern in this case.
 *
 * @export
 * @class LoggingAspect
 */
export class LoggerAspect {
  /**
   * static reference to logger service
   *
   * @static
   * @type {LoggerService}
   * @memberof LoggerAspect
   */
  public static logger: LoggerService;
  /**
   * static reference to object service
   *
   * @static
   * @type {ObjectService}
   * @memberof LoggerAspect
   */
  public static objectService: ObjectService;

  /**
   * Skips the below aspects if logger is not enabled.
   *
   * @static
   * @type {boolean}
   * @memberof LoggerAspect
   */
  public static isLoggerEnable: boolean;

  /**
   * Gets the module name from woven data
   *
   * @private
   * @static
   * @param {*} woveMetadata
   * @returns {string}
   * @memberof LoggerAspect
   */
  private static getModuleName(woveMetadata: any): string {
    return LoggerAspect.objectService.get(woveMetadata, 'moduleName') || 'Module name not passed';
  }

  /**
   * before each function
   *
   * @param {Metadata} meta
   * @memberof LoggerAspect
   */
  @beforeMethod({
    classNamePattern: /.*/,
    methodNamePattern: /^((?!ngAfterViewChecked|ngAfterContentChecked|canDeactivate|ngDoCheck).)*$/,
  })
  public before(meta: Metadata): void {
    if (LoggerAspect.isLoggerEnable) {
      const moduleName: string = LoggerAspect.getModuleName(meta.woveMetadata);
      const args: string[] = [];

      for (let arg of meta.method.args) {
        if (typeof arg === 'object' || Array.isArray(arg)) {
          arg = CircularJSON.stringify(arg);
        }
        args.push(arg);
      }
      if (LoggerAspect.logger) {
        LoggerAspect.logger.debug(
          moduleName,
          meta.className,
          `Entering ${meta.method.name} in ${meta.className}
                    with args: ${args.join(', ')}.`
        );
      }
    }
  }

  /**
   * After each function
   *
   * @param {Metadata} meta
   * @memberof LoggerAspect
   */
  @afterMethod({
    classNamePattern: /.*/,
    methodNamePattern: /^((?!ngAfterViewChecked|ngAfterContentChecked|ngDoCheck).)*$/,
  })
  public after(meta: Metadata): void {
    if (LoggerAspect.isLoggerEnable) {
      const moduleName: string = LoggerAspect.getModuleName(meta.woveMetadata);
      let result: any = meta.method.result;

      if (result instanceof Observable) {
        // TODO: We can theoritically subscribe to and capture API errors
        // and returns from this.  We need to make sure we are
        // Un subscribing to prevent memory-leaks.
        result = CircularJSON.stringify(result);
      } else if (typeof result === 'object' || Array.isArray(result)) {
        result = CircularJSON.stringify(result);
      }

      if (LoggerAspect.logger) {
        LoggerAspect.logger.debug(
          moduleName,
          meta.className,
          `leaving ${meta.method.name} in ${meta.className} with response: ${result}`
        );
      }
    }
  }

  /**
   * On exception throw, can we catch errors here?
   *
   * @param {Metadata} meta
   * @memberof LoggerAspect
   */
  @onThrowOfMethod({
    methodNamePattern: /.*/,
    classNamePattern: /.*/,
  })
  public onThrow(meta: Metadata): void {
    const moduleName: string = LoggerAspect.getModuleName(meta.woveMetadata);
    if (LoggerAspect.logger) {
      LoggerAspect.logger.fatal(
        moduleName,
        meta.className,
        `Exception thrown `,
        meta.method.exception.code,
        meta.method.exception
      );
    }
  }
}
